import React, { useEffect, useState } from 'react';
import { Check, Circle, Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { DeliveryProjectPanel, DepartmentDecision, DepartmentReadiness } from '../../types/aai';
import { Action, Chip, Drawer, Kicker, Loading } from '../../components/aa';
import { relativeTime } from '../partner/format';

/*
 * The head's decision, minimal. One request, the evidence needed to decide it,
 * the confidentiality boundary, and three actions. Nothing else from the
 * Project record: quotes, controls and delivery notes stay in the record, which
 * the head can still open from Projects.
 */

type Mode = 'read' | 'changes' | 'cannot';

/*
 * The size of what is being agreed to.
 *
 * A Quick Win is one small tool for one team. A Delivery Project is
 * coordinated work that needs a named team and a price the client accepts.
 * The head clears the first gate on both, and until now their screen looked
 * identical either way, so they were approving without seeing which.
 *
 * A request raised before Orbit routed anything, and every shadow-AI capture,
 * carries no route. That says "nobody sized this", which is worth showing as
 * itself rather than rounding down to the smaller of the two.
 */
const ROUTE: Record<string, { label: string; means: string }> = {
  quick_win: {
    label: 'Quick Win',
    means: 'One focused result. Delivered by one person, no quote and no delivery team.',
  },
  project: {
    label: 'Delivery Project',
    means: 'Coordinated work. It needs a named team and a price the company accepts before anything is delivered.',
  },
};
/* The other half of "what you are approving": what shape the thing would be.
   Same rule as the route above, a missing value says nobody classified it
   rather than quietly defaulting to the commonest answer. */
const SHAPE: Record<string, { label: string; means: string }> = {
  skill: { label: 'Skill', means: 'One repeated task, saved so anyone allowed can run it again.' },
  plugin: { label: 'Plugin', means: 'Several steps across several apps, with their connections, held together as one thing.' },
  document: { label: 'Document', means: 'The piece of work itself, produced once.' },
  routine: { label: 'Routine', means: 'Work that runs on a schedule, with an owner watching it.' },
};
const FIELD = 'w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[13.5px] leading-[21px] text-ink outline-none placeholder:text-ink-soft focus:border-ink';

interface Evidence {
  label: string;
  ok: boolean;
}

const evidenceFor = (decision: DepartmentDecision, panel: DeliveryProjectPanel | null, readiness: DepartmentReadiness | null): Evidence[] => {
  const items: Evidence[] = [
    { label: decision.detail ? 'Request describes the work and who it is for' : 'Request has no description yet', ok: Boolean(decision.detail) },
  ];
  for (const control of (panel?.controls || []).slice(0, 2)) {
    items.push({ label: control.value || control.unset, ok: Boolean(control.value) });
  }
  if (readiness && !readiness.suppressed && readiness.maturity.maturity_pct !== null) {
    items.push({ label: `${readiness.maturity.maturity_pct}% role readiness across the team`, ok: readiness.maturity.maturity_pct >= 50 });
  } else {
    items.push({ label: 'Team readiness is protected until more people are onboarded', ok: false });
  }
  return items;
};

export const DecisionDrawer: React.FC<{
  decision: DepartmentDecision;
  department: string;
  isLead: boolean;
  readiness: DepartmentReadiness | null;
  onClose: () => void;
  onDecided: () => void;
}> = ({ decision, department, isLead, readiness, onClose, onDecided }) => {
  const [panel, setPanel] = useState<DeliveryProjectPanel | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<Mode>('read');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    aaiApi.getDeliveryProject(decision.request_id)
      .then((res) => { if (live) setPanel(res.data); })
      .catch(() => { if (live) setPanel(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [decision.request_id]);

  const decide = async (kind: 'approved' | 'needs_info' | 'declined', done: string) => {
    if (kind !== 'approved' && !note.trim()) {
      toast.error('Say why, in a sentence. The requester reads it.');
      return;
    }
    setBusy(true);
    try {
      await aaiApi.decideRequest(decision.request_id, { decision: kind, comment: note.trim() || undefined });
      toast.success(done);
      window.dispatchEvent(new Event('aai:approvals-changed'));
      onDecided();
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      toast.error(detail || 'That decision did not save.');
    } finally {
      setBusy(false);
    }
  };

  let statusChip = <Chip>Open</Chip>;
  if (decision.status === 'needs_info') statusChip = <Chip tone="warn">Detail requested</Chip>;
  const evidence = evidenceFor(decision, panel, readiness);
  const route = ROUTE[decision.route || ''];
  const shape = SHAPE[decision.deliverable || ''];

  let footer: React.ReactNode = (
    <div className="flex flex-wrap items-center gap-3">
      <Action size="lg" onClick={() => decide('approved', 'Cleared. The company decides next; it is not a Project yet.')} disabled={busy} reason="Saving">Approve decision</Action>
      <Action size="lg" variant="quiet" onClick={() => setMode('changes')}>Request changes</Action>
      <Action size="lg" variant="ghost" onClick={() => setMode('cannot')}>Cannot review</Action>
    </div>
  );
  if (!isLead) {
    footer = (
      <div className="flex flex-wrap items-center gap-3">
        <Action size="lg" disabled reason={`The recorded ${department} lead gives the first approval`}>Approve decision</Action>
        <Action size="lg" variant="ghost" onClick={onClose}>Close</Action>
      </div>
    );
  }
  if (mode !== 'read') {
    let confirmLabel = 'Send request for changes';
    let confirmKind: 'needs_info' | 'declined' = 'needs_info';
    let done = 'Sent back to the requester with your note.';
    if (mode === 'cannot') {
      confirmLabel = 'Decline and explain';
      confirmKind = 'declined';
      done = 'Declined, with your note on the record.';
    }
    footer = (
      <div className="flex flex-wrap items-center gap-3">
        <Action size="lg" onClick={() => decide(confirmKind, done)} disabled={busy} reason="Saving">{confirmLabel}</Action>
        <Action size="lg" variant="ghost" onClick={() => { setMode('read'); setNote(''); }}>Back</Action>
      </div>
    );
  }

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Needs your review</Kicker>}
      badge={statusChip}
      title={decision.title}
      sub={`${department} · raised ${relativeTime(decision.since)}`}
      footer={footer}
    >
      <div className="rounded-card border-l-[3px] border-accent-500 bg-[rgba(255,234,0,0.08)] px-5 py-4">
        <div className="flex items-start gap-3">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent-500 text-black">
            {isLead && <Check size={16} strokeWidth={3} />}
            {!isLead && <Lock size={14} strokeWidth={2.5} />}
          </span>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{isLead ? 'Assigned to you' : 'Assigned to the recorded lead'}</div>
            <div className="mt-1 text-[14px] font-semibold text-ink">
              {isLead && `You own the first approval for ${department} and the affected team.`}
              {!isLead && `You can read the evidence. The recorded ${department} lead clears it.`}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-panel border border-line bg-white px-5 py-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">What you are approving</div>
        <div className="mt-2 flex flex-wrap items-center gap-2.5">
          {route && <Chip tone="ok">{route.label}</Chip>}
          {!route && <Chip tone="private">Not routed</Chip>}
          {shape && <Chip tone="ok">{shape.label}</Chip>}
          {!shape && <Chip tone="private">Shape not set</Chip>}
          <span className="text-[12.5px] text-ink-dim">Orbit's recommendation from the answers the requester gave.</span>
        </div>
        <p className="m-0 mt-2 text-[12.5px] leading-[19px] text-ink-dim">
          {route && route.means}
          {!route && 'This was raised before Orbit sized requests, so its size is unknown. Ask for detail if that changes your decision.'}
        </p>
        <p className="m-0 mt-1.5 text-[12.5px] leading-[19px] text-ink-dim">
          {shape && shape.means}
          {!shape && 'Nobody has said what shape this would be built as. Ask for detail if that changes your decision.'}
        </p>
      </div>

      <div className="mt-5 rounded-panel border border-line bg-white px-5 py-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Evidence needed for this decision</div>
        {loading && <div className="mt-3"><Loading label="Reading the request" /></div>}
        {!loading && (
          <div className="mt-3 flex flex-col gap-2">
            {evidence.map((item) => (
              <div key={item.label} className="flex items-center gap-3 rounded-card border border-line px-4 py-3 text-[13.5px] font-semibold text-ink">
                {item.ok && <Check size={16} strokeWidth={3} className="shrink-0 text-[#3F7A2F]" />}
                {!item.ok && <Circle size={14} className="shrink-0 text-ink-soft" />}
                {item.label}
              </div>
            ))}
          </div>
        )}
        {decision.est_value_band && (
          <p className="m-0 mt-3 text-[12.5px] leading-[19px] text-ink-dim">Potential value {decision.est_value_band}. A forecast until reviewed evidence confirms it.</p>
        )}
      </div>

      {mode !== 'read' && (
        <div className="mt-5">
          <div className="text-[13px] font-semibold text-ink">{mode === 'cannot' ? 'Why you cannot review this' : 'What should change'}</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder={mode === 'cannot' ? 'One sentence the requester can act on.' : 'How many hours a month does this take today, and for whom?'}
            className={`${FIELD} mt-2`}
          />
        </div>
      )}

      <div className="mt-5 flex items-start gap-3.5 rounded-panel bg-canvas-inset px-5 py-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-tile bg-ink text-white"><Lock size={15} strokeWidth={2} /></span>
        <div>
          <div className="text-[12.5px] text-ink-dim">Confidentiality boundary</div>
          <div className="mt-0.5 text-[14px] font-semibold text-ink">Minimum necessary context only</div>
          <p className="m-0 mt-1 text-[12.5px] leading-[19px] text-ink-dim">You see the decision evidence, not private employee requests or unrelated Delivery Project documents.</p>
        </div>
      </div>
    </Drawer>
  );
};
